import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity, ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw,
  CheckCircle2, XCircle, ArrowRight,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';

/**
 * AdminActivityFeed — compact "last 50 admin actions" timeline.
 *
 * Pulls from /api/ultra-admin/audit-logs and surfaces three classes of events
 * the operator cares about most:
 *   • SUCCESS  → routine action, neutral pill
 *   • BLOCKED  → ownership-rule violation (red pill)
 *   • OVERRIDE → super admin acted on another admin's territory (amber pill)
 *
 * Narrative line for each event explains "who did what to whom" in one
 * sentence, so the operator never has to expand a JSON blob to triage a
 * conflict.
 */

// ── Action label registry — extends ACTION_CONFIG from AuditLogsPage with the
//    new ownership rule actions. Tuple: [trLabel, enLabel].
const ACTION_LABELS = {
  // ── Stand Assignment Ownership Rule (Phase 68) ────────────────────────────
  'stand_assigned':          ['Stand atandı',           'Stand assigned'],
  'stand_unassigned':        ['Atama kaldırıldı',       'Stand unassigned'],
  'stand_deleted':           ['Stand silindi',          'Stand deleted'],
  'stand_assign_blocked':    ['Atama engellendi',       'Assign blocked'],
  'stand_unassign_blocked':  ['Atama kaldırma engellendi','Unassign blocked'],
  'stand_update_blocked':    ['Düzenleme engellendi',   'Edit blocked'],
  'stand_delete_blocked':    ['Silme engellendi',       'Delete blocked'],
  // ── Stand Change Request Ownership Rule (Phase 69) ────────────────────────
  'stand_change_request.approved':         ['Talep onaylandı',          'Request approved'],
  'stand_change_request.rejected':         ['Talep reddedildi',         'Request rejected'],
  'stand_change_request.proposed':         ['Alternatif önerildi',      'Alternatives proposed'],
  'stand_change_request.review_blocked':   ['İnceleme engellendi',      'Review blocked'],
  'stand_change_request.propose_blocked':  ['Öneri engellendi',         'Proposal blocked'],
  // ── Super Admin Lifecycle (Phase 71) ──────────────────────────────────────
  'super_admin.created':            ['Süper Admin eklendi',          'Super Admin created'],
  'super_admin.invited':            ['Davet gönderildi',             'Invitation sent'],
  'super_admin.deactivated':        ['Süper Admin pasife alındı',    'Super Admin deactivated'],
  'super_admin.activated':          ['Süper Admin aktife alındı',    'Super Admin activated'],
  'super_admin.deleted':            ['Süper Admin silindi',          'Super Admin deleted'],
  'super_admin.password_set':       ['Şifre belirlendi',             'Password set'],
  // ── Pre-existing audit actions ─────────────────────────────────────────────
  'registration.approved':    ['Başvuru onaylandı',     'Registration approved'],
  'registration.rejected':    ['Başvuru reddedildi',    'Registration rejected'],
  'tenant.created':           ['Tenant oluşturuldu',    'Tenant created'],
  'tenant.updated':           ['Tenant güncellendi',    'Tenant updated'],
  'tenant.archived':          ['Tenant arşivlendi',     'Tenant archived'],
  'tenant.unarchived':        ['Tenant geri alındı',    'Tenant restored'],
  'user.created':             ['Kullanıcı oluşturuldu', 'User created'],
  'user.deleted':             ['Kullanıcı silindi',     'User deleted'],
  // ── Phase 86AT — Manual zone user creation ────────────────────────────────
  'user.green_zone_manual_create':         ['Green Zone müşterisi oluşturuldu',  'Green Zone customer created'],
  'user.green_zone_invite_sent':           ['Green Zone daveti gönderildi',      'Green Zone invite sent'],
  'user.green_zone_invite_failed':         ['Green Zone daveti gönderilemedi',   'Green Zone invite failed'],
  // ── Phase 86AZ.58/59 — Manual supplier & contractor onboarding ────────────
  'user.supplier_manual_create':           ['Tedarikçi oluşturuldu',             'Supplier created'],
  'user.supplier_invite_resent':           ['Tedarikçi daveti yeniden gönderildi','Supplier invite re-sent'],
  'user.supplier_invite_resend_failed':    ['Tedarikçi daveti gönderilemedi',    'Supplier invite failed'],
  'supplier.password_set':                 ['Tedarikçi şifresini belirledi',     'Supplier set password'],
  'user.contractor_manual_create':         ['Müteahhit oluşturuldu',             'Contractor created'],
  'user.contractor_invite_resent':         ['Müteahhit daveti yeniden gönderildi','Contractor invite re-sent'],
  'user.contractor_invite_resend_failed':  ['Müteahhit daveti gönderilemedi',    'Contractor invite failed'],
  'contractor.password_set':               ['Müteahhit şifresini belirledi',     'Contractor set password'],
};

function actionLabel(action, tr) {
  const pair = ACTION_LABELS[action];
  if (pair) return tr ? pair[0] : pair[1];
  return action;
}

function formatTime(iso, tr) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString(tr ? 'tr-TR' : 'en-US', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

/** Classify an audit entry into 'blocked' | 'override' | 'success'. */
function classify(log) {
  const outcome = (log.details || {}).outcome;
  if (outcome === 'blocked' || /_blocked$/.test(log.action || '')) return 'blocked';
  if ((log.details || {}).overrode_owner) return 'override';
  return 'success';
}

/** Build a one-sentence narrative for the activity row. */
function narrative(log, tr) {
  const d = log.details || {};
  const actor = log.actor_email || '?';
  const target = log.target_name || d.tenant_name || '';
  const klass = classify(log);

  if (klass === 'blocked') {
    const reason = d.reason || '';
    const ownerMatch = reason.match(/=([^=]+)$/);
    const owner = ownerMatch ? ownerMatch[1] : (d.owner || '');
    if (tr) {
      return owner
        ? `${actor}, ${owner} adlı admin'in işine müdahale etmek istedi (${target || '—'}). Engellendi.`
        : `${actor} engellendi: ${target || '—'}.`;
    }
    return owner
      ? `${actor} tried to override ${owner}'s action on ${target || '—'}. Blocked.`
      : `${actor} blocked: ${target || '—'}.`;
  }

  if (klass === 'override') {
    const owner = d.overrode_owner || '';
    if (tr) {
      return `${actor} (Super Admin), ${owner} adlı admin'in işine müdahale etti${target ? ` — ${target}` : ''}.`;
    }
    return `${actor} (Super Admin) overrode ${owner}'s territory${target ? ` — ${target}` : ''}.`;
  }

  if (target) {
    return tr ? `${actor} → ${target}` : `${actor} → ${target}`;
  }
  return actor;
}

const KIND_STYLE = {
  blocked:  {
    pill:   'bg-rose-100 text-rose-700 border border-rose-200',
    Icon:   ShieldAlert,
    iconCls: 'text-rose-600',
    cardCls: 'border-rose-100 bg-rose-50/40',
    label:  ['ENGELLENDİ', 'BLOCKED'],
  },
  override: {
    pill:   'bg-amber-100 text-amber-800 border border-amber-200',
    Icon:   AlertTriangle,
    iconCls: 'text-amber-600',
    cardCls: 'border-amber-100 bg-amber-50/40',
    label:  ['OVERRIDE', 'OVERRIDE'],
  },
  success:  {
    pill:   'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Icon:   CheckCircle2,
    iconCls: 'text-emerald-500',
    cardCls: 'border-gray-100 bg-white',
    label:  ['BAŞARILI', 'SUCCESS'],
  },
};

export default function AdminActivityFeed({ tr, onOpenFullLog }) {
  const [logs, setLogs]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState('');
  const [filter, setFilter] = useState('all'); // all | conflicts

  const fetchLogs = useCallback(async () => {
    setLoad(true);
    setError('');
    try {
      const r = await apiClient.get('/ultra-admin/audit-logs?limit=50&page=1');
      setLogs(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || (tr ? 'Aktivite yüklenemedi.' : 'Failed to load activity.'));
    } finally {
      setLoad(false);
    }
  }, [tr]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const visible = filter === 'conflicts'
    ? logs.filter(l => classify(l) !== 'success')
    : logs;

  const conflictCount = logs.filter(l => classify(l) !== 'success').length;

  return (
    <div className="space-y-4" data-testid="admin-activity-feed">
      {/* Header — title + filters + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-600" />
          <h3 className="font-bold text-gray-800 text-lg">
            {tr ? 'Yönetici Aktivitesi' : 'Admin Activity'}
          </h3>
          <span className="text-xs text-gray-400 ml-2">
            {tr ? `Son ${logs.length} aksiyon` : `Last ${logs.length} actions`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl bg-gray-100 p-1 text-xs">
            <button
              data-testid="activity-filter-all"
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-lg transition-colors ${filter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {tr ? 'Tümü' : 'All'}
            </button>
            <button
              data-testid="activity-filter-conflicts"
              onClick={() => setFilter('conflicts')}
              className={`px-3 py-1 rounded-lg flex items-center gap-1.5 transition-colors ${filter === 'conflicts' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <ShieldAlert className="h-3 w-3" />
              {tr ? 'Çatışmalar' : 'Conflicts'}
              {conflictCount > 0 && (
                <span className="bg-rose-500 text-white px-1.5 rounded-full text-[10px] font-bold">
                  {conflictCount}
                </span>
              )}
            </button>
          </div>
          <button
            data-testid="activity-refresh"
            onClick={fetchLogs}
            disabled={loading}
            className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
            title={tr ? 'Yenile' : 'Refresh'}>
            <RefreshCw className={`h-3.5 w-3.5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <a
            href="/ultra-admin/audit-logs"
            data-testid="activity-open-full-log"
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1">
            {tr ? 'Tam log' : 'Full log'} <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* Body */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 flex items-center gap-2">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading && logs.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          {tr ? 'Aktivite yükleniyor…' : 'Loading activity…'}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-2xl">
          {filter === 'conflicts'
            ? (tr ? 'Çatışmalı aksiyon yok. 🎉' : 'No conflicting actions. 🎉')
            : (tr ? 'Henüz aktivite yok.' : 'No activity yet.')}
        </div>
      ) : (
        <ol className="space-y-2">
          {visible.map((log) => {
            const kind  = classify(log);
            const style = KIND_STYLE[kind];
            const Icon  = style.Icon;
            return (
              <li
                key={log.id}
                data-testid={`activity-row-${log.id}`}
                data-kind={kind}
                className={`rounded-xl border ${style.cardCls} px-3 py-2.5 flex items-start gap-3`}>
                <div className={`mt-0.5 flex-shrink-0 ${style.iconCls}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs mb-0.5">
                    <span className={`px-2 py-0.5 rounded-full font-bold tracking-wide ${style.pill}`}>
                      {tr ? style.label[0] : style.label[1]}
                    </span>
                    <span className="font-medium text-gray-700">
                      {actionLabel(log.action, tr)}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500" title={log.created_at}>
                      {formatTime(log.created_at, tr)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 truncate">
                    {narrative(log, tr)}
                  </p>
                  {log.details && (log.details.reason || log.details.overrode_owner) && (
                    <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                      {log.details.reason || (tr ? `Üzerine yazılan sahip: ${log.details.overrode_owner}` : `Overrode owner: ${log.details.overrode_owner}`)}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export { ACTION_LABELS as ADMIN_ACTIVITY_ACTION_LABELS };
